/**
 * `P0-UIK-014`: the side panel route mechanism.
 *
 * 4.6.4, for the console: *"The conversation is a **side panel route**
 * (`?panel=conversation`), full screen height, bounded scroll, composer
 * pinned. The page never grows with the number of messages."*
 *
 * **Why a ROUTE and not a piece of component state.** Three things follow from
 * the panel being in the URL and none of them follow from a `useState`. It
 * survives a reload, which is what a staff member does when a page misbehaves
 * mid-conversation. It can be linked , *"look at this thread"* is a paste, not
 * a set of directions. And the browser's back button closes it, which is what
 * every user already expects and what a state flag makes impossible: back
 * would leave the screen entirely with the panel still open behind it.
 *
 * **Danger sentence.** The panel is a SECOND thing on screen, not a different
 * screen. If opening it changed the path, the whitelist would have to grant a
 * conversation screen separately, the nav rail would grow an entry for
 * something that is not a destination, and closing the panel would need to
 * remember which screen you were on. Keeping it in the query string means the
 * screen underneath never changes, and its guard already ran.
 *
 * This file holds no chrome and no React: what a panel LOOKS like is the
 * shell's business (`ConsoleShell`), what it MEANS is here.
 */

/** The panels the console can open beside a screen. `conversation` is 4.6.4's own. */
export const PANNEAUX = ["conversation"] as const;

export type Panneau = (typeof PANNEAUX)[number];

export const PANEL_PARAM = "panel";

const estPanneau = (value: string): value is Panneau => (PANNEAUX as readonly string[]).includes(value);

/**
 * The panel a URL asks for, or `null`.
 *
 * An UNKNOWN value reads as no panel rather than throwing. A URL is something a
 * person types, edits and shares, so `?panel=conversatoin` is a typo, not an
 * attack, and the right answer is the screen without a panel , never an error
 * screen over content the caller is perfectly entitled to see.
 */
export function panneauDe(search: string): Panneau | null {
  const value = new URLSearchParams(search.startsWith("?") ? search.slice(1) : search).get(PANEL_PARAM);
  if (value === null) return null;
  return estPanneau(value) ? value : null;
}

/** The same URL with a panel open. The path and every other parameter survive. */
export function avecPanneau(search: string, panneau: Panneau): string {
  const params = new URLSearchParams(search.startsWith("?") ? search.slice(1) : search);
  params.set(PANEL_PARAM, panneau);
  return `?${params.toString()}`;
}

/**
 * The same URL with the panel closed.
 *
 * Returns `""` rather than `"?"` when nothing is left, because a bare `?` is a
 * different string that renders as a different history entry while looking
 * identical in the address bar , the sort of difference that makes a back
 * button need pressing twice.
 */
export function sansPanneau(search: string): string {
  const params = new URLSearchParams(search.startsWith("?") ? search.slice(1) : search);
  params.delete(PANEL_PARAM);
  const rest = params.toString();
  return rest === "" ? "" : `?${rest}`;
}
